import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Input, Button, Typography, Card, Alert, message } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { changePassword } from '../profile/api';
import { useAuth } from '../../context/AuthContext';

const { Title, Text } = Typography;

/**
 * The screen a user is pinned to when their PASSWORD_EXPIRY login policy says
 * their password is overdue.
 *
 * They ARE signed in (the token is valid) but the backend refuses every request
 * except this one, so there is nothing to gain by navigating away — the only
 * ways out are changing the password or signing out. Rendered outside
 * MainLayout deliberately: no sidebar, no menu, nowhere else to click.
 */
const ForcePasswordChange = () => {
  const navigate = useNavigate();
  const { login, user, logout } = useAuth();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Stashed by Login.jsx so the message matches what the policy actually said.
  const reason = sessionStorage.getItem('passwordChangeReason')
    || 'Your password has expired. Please set a new password to continue.';

  const onFinish = async (values) => {
    setLoading(true);
    setError('');
    try {
      const res = await changePassword({
        currentPassword: values.currentPassword,
        password: values.newPassword,
      });
      // The backend hands back a token WITHOUT the "must change password"
      // claim — swap it in so the user carries straight on to the app.
      if (res?.token) login({ ...(user || {}), token: res.token, mustChangePassword: false });
      sessionStorage.removeItem('passwordChangeReason');
      message.success('Password updated. Welcome back.');
      navigate('/dashboard', { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.message || 'Could not update your password. Please try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const onSignOut = () => {
    sessionStorage.removeItem('passwordChangeReason');
    logout();
    navigate('/login', { replace: true });
  };

  return (
    <div style={{ display: 'flex', justifyContent: 'center', marginTop: 80 }}>
      <Card style={{ width: 440 }}>
        <Title level={3} style={{ textAlign: 'center', marginBottom: 4 }}>Set a New Password</Title>
        <Text type="secondary" style={{ display: 'block', textAlign: 'center', marginBottom: 20 }}>
          Required by your organisation's login policy
        </Text>

        <Alert type="warning" showIcon message={reason} style={{ marginBottom: 20 }} />

        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item
            label="Current Password"
            name="currentPassword"
            rules={[{ required: true, message: 'Enter the password you just signed in with' }]}
          >
            <Input.Password prefix={<LockOutlined />} autoComplete="current-password" />
          </Form.Item>

          <Form.Item
            label="New Password"
            name="newPassword"
            rules={[
              { required: true, message: 'Enter a new password' },
              { min: 8, message: 'Use at least 8 characters' },
            ]}
          >
            <Input.Password prefix={<LockOutlined />} autoComplete="new-password" />
          </Form.Item>

          <Form.Item
            label="Confirm New Password"
            name="confirmPassword"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: 'Re-enter the new password' },
              ({ getFieldValue }) => ({
                validator: (_, value) => (!value || getFieldValue('newPassword') === value
                  ? Promise.resolve()
                  : Promise.reject(new Error('Passwords do not match'))),
              }),
            ]}
          >
            <Input.Password prefix={<LockOutlined />} autoComplete="new-password" />
          </Form.Item>

          {error && <Text type="danger" style={{ display: 'block', marginBottom: 12 }}>{error}</Text>}

          <Form.Item style={{ marginBottom: 8 }}>
            <Button type="primary" htmlType="submit" block loading={loading}>
              Update Password &amp; Continue
            </Button>
          </Form.Item>
        </Form>

        <Button type="link" block onClick={onSignOut}>Sign out instead</Button>
      </Card>
    </div>
  );
};

export default ForcePasswordChange;
